import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { forwardRef, useState, useEffect } from "react";
import { InputAdornment } from "@mui/material";
import { InputProps } from "../common/inputProps";

interface InputMoneyProps extends InputProps {
  prefix?: string;
  decimalSeparator?: string;
  groupSeparator?: string;
  adornmentColor?: string;
  adornmentBackgroundColor?: string;
  // Adicionar propriedade para o callback de evento de digitação
  onTypingChange?: (name: string, value: any) => void;
}

const InputMoney = forwardRef<HTMLInputElement, InputMoneyProps>(({ 
    error, 
    placeholder, 
    value, 
    type = "text", 
    width = 300, 
    height = 55, 
    onChange, 
    onBlur, 
    name = "", 
    disabled = false, 
    required = false,
    icon = false,
    prefix = "R$ ",
    decimalSeparator = ",",
    groupSeparator = ".",
    onTypingChange, // Nova prop para notificar mudanças durante a digitação
    ...rest 
  }, ref) => {
    
    const [displayValue, setDisplayValue] = useState<string>('');
    // Referência para o timeout do debounce
    const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
    
    const formatCurrency = (value: number): string => {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    
    useEffect(() => {
      if (value !== undefined && value !== null && value !== '') {
        const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
        if (!isNaN(numValue)) {
          setDisplayValue(formatCurrency(numValue));
        } else {
          setDisplayValue('0,00');
        }
      } else {
        setDisplayValue('0,00');
      }
    }, [value]);
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let inputValue = e.target.value;
      
      if (!inputValue) {
        setDisplayValue('');
        const emptyValue = '';
        
        // Atualizar imediatamente para o componente pai
        if (onChange) {
          const event = {
            target: {
              name,
              value: emptyValue
            }
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          
          onChange(event);
        }
        
        // Notificar sobre a mudança durante a digitação (sem debounce para campos vazios)
        if (onTypingChange) {
          onTypingChange(name, emptyValue);
        }
        
        return;
      }
      
      const digitsOnly = inputValue.replace(/\D/g, '');
      
      if (digitsOnly.length === 0) {
        setDisplayValue('');
        const zeroValue = 0;
        
        // Atualizar imediatamente para o componente pai
        if (onChange) {
          const event = {
            target: {
              name,
              value: zeroValue
            }
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          
          onChange(event);
        }
        
        // Notificar sobre a mudança durante a digitação (sem debounce para valores zero)
        if (onTypingChange) {
          onTypingChange(name, zeroValue);
        }
        
        return;
      }
      
      const paddedDigits = digitsOnly.padStart(3, '0');
      
      const integerPart = paddedDigits.slice(0, paddedDigits.length - 2);
      const decimalPart = paddedDigits.slice(paddedDigits.length - 2);
      
      let formattedInteger = '';

      for (let i = 0; i < integerPart.length; i++) {
        if (i > 0 && (integerPart.length - i) % 3 === 0) {
          formattedInteger += groupSeparator;
        }
        formattedInteger += integerPart[i];
      }
      
      const formatted = `${formattedInteger}${decimalSeparator}${decimalPart}`;
      setDisplayValue(formatted);
      
      const numericValue = Number(`${integerPart}.${decimalPart}`);
      
      // Atualizar imediatamente para o componente pai
      if (onChange) {
        const event = {
          target: {
            name,
            value: numericValue
          }
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        
        onChange(event);
      }
      
      // Aplicar debounce para notificar sobre a mudança durante a digitação
      if (onTypingChange) {
        // Cancelar o timeout anterior se existir
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
        
        // Configurar um novo timeout
        const timeout = setTimeout(() => {
          onTypingChange(name, numericValue);
        }, 500); // 500ms de debounce
        
        setDebounceTimeout(timeout);
      }
    };
    
    // Limpar o timeout quando o componente for desmontado
    useEffect(() => {
      return () => {
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
      };
    }, [debounceTimeout]);
    
    return (
      <Box sx={{ width: width }}>
        <TextField
          label={placeholder}
          name={name}
          error={!!error}
          disabled={disabled}
          required={required}
          variant="outlined"
          fullWidth
          className={`container-input__input ${error ? "container-input__input__error" : "container-input__input__default"}`}
          sx={{
            display: 'flex', 
            alignItems: 'flex-end',
            width: "100%",
            height: height,
            backgroundColor: "#fff",
          }}
          InputProps={{
            value: displayValue,
            onChange: handleInputChange,
            startAdornment: (
              <InputAdornment position="start" sx={{marginTop: "-2px"}}>
                  {prefix}
              </InputAdornment>
            ),
            ...rest
          }}
          inputRef={ref}
          onBlur={onBlur}
        />
        {error && (<span className="container-input__input__error-message">{error.toString()}</span>)}
      </Box>
    );
  }
);

export default InputMoney;
